import {useState} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {envVars} from '@/envVars.ts'
import {cn} from '@utilities/helpers'
import {Container} from '@components/ui/container.tsx'
import {XCloseIcon} from '@components/ui/icon.tsx'
import {useMe} from '@features/user/queries/useMe.ts'
import {useFloorPlanOptin} from '@features/floor-plan/queries/useFloorPlanOptin.ts'
import {FloorPlanOptInButton} from '@features/floor-plan/components/floor-plan-optin-button'
import {showFloorPlanSettingsToast} from '@features/floor-plan/components/floor-plan-toast'
import {
    floorPlanBannerDismissedKey,
    FLOOR_PLAN_LINK_CLASSNAME,
    FLOOR_PLAN_SCANNING_GUIDELINES_URL
} from '@features/floor-plan/constants.ts'

export const FloorPlanBanner = () => {
    const {t} = useTranslation()
    const {data: user} = useMe()
    const {isUndecided} = useFloorPlanOptin()
    const [isDismissed, setIsDismissed] = useState(false)

    const dismissKey = user ? floorPlanBannerDismissedKey(user.id) : null
    const isAlreadyDismissed = dismissKey ? localStorage.getItem(dismissKey) === 'true' : false

    if (!envVars.VITE_IS_FLOOR_PLAN_ENABLED || !isUndecided || isDismissed || isAlreadyDismissed) {
        return null
    }

    const persistDismiss = () => {
        if (dismissKey) {
            localStorage.setItem(dismissKey, 'true')
        }
        setIsDismissed(true)
    }

    // The X also shows the toast; opt-in only dismisses (its toast comes from
    // FloorPlanOptInButton, shared with the settings).
    const handleDismiss = () => {
        persistDismiss()
        showFloorPlanSettingsToast({
            title: t('floor_plan:toast:title'),
            description: t('floor_plan:toast:description')
        })
    }

    return (
        <Container>
            <div className="relative flex flex-col items-stretch gap-4 rounded-xl border border-neutral-200 bg-white p-5 md:flex-row md:items-center">
                <div className="flex flex-1 flex-col gap-2 pr-6">
                    <h3 className="text-lg font-semibold text-neutral-900">{t('floor_plan:title')}</h3>
                    <p className="text-sm text-neutral-700">{t('floor_plan:banner:description')}</p>
                    <p className="text-sm text-neutral-700">
                        <Trans
                            i18nKey="floor_plan:guidelines"
                            components={{
                                guidelines: (
                                    <a
                                        className={cn(FLOOR_PLAN_LINK_CLASSNAME, 'text-sm')}
                                        href={FLOOR_PLAN_SCANNING_GUIDELINES_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                )
                            }}
                        />
                    </p>
                </div>
                <div className="flex items-center [&>a]:w-full [&>button]:w-full [&>a]:md:w-fit [&>button]:md:w-fit">
                    <FloorPlanOptInButton from="home" />
                </div>
                <button
                    type="button"
                    aria-label={t('commons:close')}
                    onClick={handleDismiss}
                    className="absolute right-4 top-4 inline-flex cursor-pointer border-none bg-none p-0 text-neutral-700 [&_svg]:h-5 [&_svg]:w-5"
                >
                    <XCloseIcon />
                </button>
            </div>
        </Container>
    )
}
